import React from 'react';
import { Award, Shield, HelpCircle } from 'lucide-react';

const PrivilegesSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  privilegeTypes 
}) => {
  if (!formData.hasPrivileges) {
    return (
      <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <h3 className="text-base font-medium text-gray-900 dark:text-white flex items-center">
            <Shield className="w-5 h-5 mr-2 text-indigo-500" />
            Налоговые льготы
          </h3>
          <div>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="hasPrivileges"
                checked={formData.hasPrivileges}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Применить налоговые льготы
              </span>
            </label>
          </div>
        </div>
        
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
          Налоговые льготы позволяют полностью или частично освободиться от уплаты налога. Они предоставляются определенным категориям граждан.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6 bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-medium text-gray-900 dark:text-white flex items-center">
          <Award className="w-5 h-5 mr-2 text-green-500" />
          Налоговые льготы
        </h3>
        <div>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="hasPrivileges"
              checked={formData.hasPrivileges}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Применить налоговые льготы
            </span>
          </label>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Тип льготы */}
        <div>
          <label htmlFor="privilegeType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Категория льготы
          </label>
          <select
            id="privilegeType"
            name="privilegeType"
            value={formData.privilegeType}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.privilegeType ? 'input-error' : ''}`}
          >
            {privilegeTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {errors.privilegeType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.privilegeType}
            </p>
          )}
        </div>
      </div>

      {/* Информация о выбранной льготе */}
      <div className="mt-4 bg-white dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center">
          <HelpCircle className="w-4 h-4 mr-1 text-green-500" />
          Информация о льготе
        </h4>
        
        {formData.privilegeType === 'none' && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Льгота не выбрана. Выберите категорию льготы для получения информации.
          </p>
        )}
        
        {formData.privilegeType === 'pensioner' && (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span className="font-medium">Пенсионеры</span> освобождаются от уплаты налога на имущество в отношении одного объекта каждого вида:
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc pl-5 space-y-1">
              <li>Квартира, часть квартиры или комната</li>
              <li>Жилой дом или часть жилого дома</li>
              <li>Гараж или машино-место</li>
              <li>Хозяйственное строение площадью до 50 кв.м.</li>
            </ul>
          </>
        )}
        
        {formData.privilegeType === 'veteran' && (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span className="font-medium">Ветераны боевых действий</span> освобождаются от уплаты налога на имущество в отношении одного объекта каждого вида недвижимости.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Льгота предоставляется на основании удостоверения ветерана боевых действий.
            </p>
          </>
        )}
        
        {formData.privilegeType === 'disabled' && (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span className="font-medium">Инвалиды I и II групп, инвалиды с детства</span> освобождаются от уплаты налога на имущество в отношении одного объекта каждого вида недвижимости.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Льгота предоставляется на основании справки, подтверждающей факт установления инвалидности.
            </p>
          </>
        )}
        
        {formData.privilegeType === 'hero' && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">Герои Советского Союза и Герои Российской Федерации</span> полностью освобождаются от уплаты налога на имущество в отношении всех объектов недвижимости, находящихся в их собственности.
          </p>
        )}
        
        {formData.privilegeType === 'chernobyl' && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">Ликвидаторы аварии на ЧАЭС</span> освобождаются от уплаты налога на имущество в отношении одного объекта каждого вида недвижимости. Льгота предоставляется на основании удостоверения участника ликвидации последствий аварии на ЧАЭС.
          </p>
        )}
        
        {formData.privilegeType === 'military' && (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span className="font-medium">Военнослужащие</span> имеют право на льготу в размере до 50% от суммы налога на имущество.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Точный размер льготы зависит от региона и устанавливается местными нормативными актами.
            </p>
          </>
        )}
        
        {formData.privilegeType === 'other' && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Иные категории льготников могут иметь право на полное или частичное освобождение от уплаты налога на имущество. Конкретные условия предоставления льгот определяются федеральным и региональным законодательством.
          </p>
        )}
      </div>

      {/* Важная информация о льготах */}
      <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
        <div className="flex items-start">
          <HelpCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 mr-3 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-yellow-700 dark:text-yellow-300 mb-1">
              Важно о налоговых льготах
            </h4>
            <p className="text-xs text-yellow-600 dark:text-yellow-400">
              Льгота предоставляется только по одному объекту каждого вида недвижимости по выбору налогоплательщика. 
              Необходимо подать заявление в налоговую инспекцию для получения льготы. Льгота не распространяется 
              на имущество, используемое в предпринимательской деятельности.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivilegesSection;